import React from 'react';
import styled from 'styled-components';
import { FiCopy, FiExternalLink, FiLayers, FiSettings, FiZap } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { copyTextToClipboard } from '../../blockchain/offlineSigning';
import { toCoreDisplayAddress } from '../../blockchain/coreIcanAddress';
import { useClientConfig } from '../../contexts/ClientConfigContext';
import { hasAssetClassDeployedContract } from '../../utils/assetClassContractUiAccess';

const Wrap = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  background: rgba(79, 172, 254, 0.06);
  border: 1px solid rgba(79, 172, 254, 0.28);
  border-radius: 8px;
  font-size: 0.85rem;

  .head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.65rem;
    margin-bottom: 0.35rem;
  }

  .label {
    color: #7dd3fc;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
  }

  .addr {
    font-family: Consolas, Monaco, monospace;
    color: #e2e8f0;
    font-size: 0.82rem;
    word-break: break-all;
  }

  .meta {
    color: #64748b;
    font-size: 0.78rem;
    line-height: 1.45;
    margin-bottom: 0.5rem;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.4rem;
    font-size: 0.82rem;

    &:last-child {
      margin-bottom: 0;
    }

    .rk {
      color: #94a3b8;
      flex-shrink: 0;
    }

    .rv {
      color: #f1f5f9;
      text-align: right;
      word-break: break-word;
      font-family: ui-monospace, monospace;
    }
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    margin-top: 0.5rem;
  }

  .or-label {
    font-size: 0.76rem;
    color: #64748b;
    margin: 0 0 0.5rem;
  }
`;

const FieldLabel = styled.label`
  display: block;
  color: #a0a0a0;
  font-size: 0.78rem;
  margin-bottom: 4px;
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(15, 15, 35, 0.9);
  color: #fff;
  font-size: 0.82rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #38bdf8;
  }
`;

function shortenAddr(addr) {
  const a = String(addr || '').trim();
  if (a.length < 18) return a || '—';
  return `${a.slice(0, 10)}…${a.slice(-8)}`;
}

/**
 * ERC-721 fraction ERC-20 (shares) — one record per authorized tokenizer per class.
 * Manual address / mint function and offline deploy live in {@link AssetClassFractionTokenDeployModal}.
 */
export function AssetClassFractionTokenBlock({
  assetClass,
  isAdmin,
  authorizedTokenizerId = null,
  selectedFractionTokenizerId = '',
  onFractionTokenizerSelectionChange,
  tokenizerPickerOptions = [],
  tokenPickerOnCard = true,
  actionClassName,
  onOpenFractionTokenModal
}) {
  const { addressExplorerUrl, explorerConfigured } = useClientConfig();

  const showEditor = isAdmin || Boolean(authorizedTokenizerId);
  const effectiveTokenizerId = isAdmin
    ? String(selectedFractionTokenizerId || '').trim()
    : String(authorizedTokenizerId || '').trim();
  const showTokenizerPicker = isAdmin && tokenPickerOnCard && tokenizerPickerOptions.length > 0;
  const adminModalOnly = isAdmin && !tokenPickerOnCard;
  const nftDeployed = hasAssetClassDeployedContract(assetClass);

  const tokenAddr = String(assetClass?.fractionTokenContractAddress || '').trim();
  const tokenDisplayAddr = tokenAddr ? toCoreDisplayAddress(tokenAddr) : '';
  const mintFn = String(assetClass?.fractionTokenMintFunctionName || 'mint').trim();

  const handleCopyToken = async () => {
    if (!tokenDisplayAddr) return;
    await copyTextToClipboard(tokenDisplayAddr);
    toast.success('Fraction token address copied');
  };

  const canOpenModal =
    typeof onOpenFractionTokenModal === 'function' &&
    nftDeployed &&
    (adminModalOnly || Boolean(effectiveTokenizerId));

  const openFractionModal = () => {
    if (adminModalOnly) {
      onOpenFractionTokenModal?.();
    } else {
      onOpenFractionTokenModal?.(effectiveTokenizerId);
    }
  };

  const showDeployCta = canOpenModal && !tokenAddr && !adminModalOnly;
  const showConfigureCta = canOpenModal && Boolean(tokenAddr) && !adminModalOnly;
  const showAdminSetupCta = canOpenModal && adminModalOnly;

  return (
    <Wrap>
      <div className="head">
        <FiLayers style={{ color: '#38bdf8' }} aria-hidden />
        <span className="label">Fraction ERC20 token (shares)</span>
      </div>
      <div className="meta">
        Each authorized tokenizer has its own ERC-20 share token contract for this class. After the NFT is locked in the
        custody vault, this contract is used to mint fractional share tokens for that locked asset.
      </div>
      {showEditor ? (
        <>
          {showTokenizerPicker ? (
            <div style={{ marginBottom: '0.65rem' }}>
              <FieldLabel>Authorized tokenizer</FieldLabel>
              <SelectInput
                value={selectedFractionTokenizerId}
                onChange={(e) => onFractionTokenizerSelectionChange?.(e.target.value)}
                aria-label="Select tokenizer for fraction token"
              >
                <option value="">Select tokenizer…</option>
                {tokenizerPickerOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </SelectInput>
              {!effectiveTokenizerId ? (
                <p className="or-label" style={{ marginTop: '0.35rem' }}>
                  Choose a tokenizer to open the fraction token setup dialog.
                </p>
              ) : null}
            </div>
          ) : null}
          {showAdminSetupCta ? (
            <>
              <div className="meta" style={{ marginBottom: '0.65rem' }}>
                Open the fraction token dialog to review <strong>all approved authorized tokenizers</strong>, select one to
                configure, then deploy <strong>RwaSharedErc20</strong> or set an existing token address (same offline flow as{' '}
                <strong>Deploy smart contract</strong> for the class NFT).
              </div>
              <div className="actions" style={{ marginTop: 0, marginBottom: 0 }}>
                <button type="button" className={actionClassName} onClick={openFractionModal}>
                  <FiZap /> Deploy fraction token contract
                </button>
              </div>
            </>
          ) : null}
          {showDeployCta ? (
            <>
              <div className="meta" style={{ marginBottom: '0.65rem' }}>
                No fraction token is linked for this tokenizer yet. Open the setup dialog to deploy{' '}
                <strong>RwaSharedErc20</strong> with offline signing or enter an existing ERC-20 address — the same pattern
                as <strong>Deploy smart contract</strong> for the class NFT.
              </div>
              <div className="actions" style={{ marginTop: 0, marginBottom: 0 }}>
                <button type="button" className={actionClassName} onClick={openFractionModal}>
                  <FiZap /> Deploy fraction token contract
                </button>
              </div>
            </>
          ) : null}
          {showConfigureCta ? (
            <div className="actions" style={{ marginTop: showDeployCta ? '0.65rem' : 0 }}>
              <button type="button" className={actionClassName} onClick={openFractionModal}>
                <FiSettings /> Configure fraction token
              </button>
            </div>
          ) : null}
          {tokenAddr ? (
            <>
              <div className="row" style={{ marginTop: '0.65rem' }}>
                <span className="rk">Token</span>
                <span className="rv" title={tokenDisplayAddr}>
                  {shortenAddr(tokenDisplayAddr)}
                </span>
              </div>
              <div className="row">
                <span className="rk">Mint function</span>
                <span className="rv">{mintFn || 'mint'}</span>
              </div>
              <div className="actions">
                <button type="button" className={actionClassName} onClick={handleCopyToken}>
                  <FiCopy /> Copy token
                </button>
                <button
                  type="button"
                  className={actionClassName}
                  disabled={!explorerConfigured}
                  title={explorerConfigured ? 'Open token in block explorer' : 'Block explorer URL not loaded from API'}
                  onClick={() => {
                    const url = addressExplorerUrl(tokenAddr);
                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <FiExternalLink /> Explorer
                </button>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <>
          <div className="row">
            <span className="rk">Token</span>
            <span className="rv" title={tokenDisplayAddr || undefined}>
              {tokenAddr ? shortenAddr(tokenDisplayAddr) : '— not set —'}
            </span>
          </div>
          <div className="row">
            <span className="rk">Mint function</span>
            <span className="rv">{mintFn || 'mint'}</span>
          </div>
          <p className="meta" style={{ marginTop: '0.5rem' }}>
            Sign in as a tokenizer operator (linked to an authorized tokenizer) or an administrator to configure this
            contract.
          </p>
          {tokenAddr ? (
            <div className="actions">
              <button type="button" className={actionClassName} onClick={handleCopyToken}>
                <FiCopy /> Copy token
              </button>
              <button
                type="button"
                className={actionClassName}
                disabled={!explorerConfigured}
                title={explorerConfigured ? 'Open token in block explorer' : 'Block explorer URL not loaded from API'}
                onClick={() => {
                  const url = addressExplorerUrl(tokenDisplayAddr || tokenAddr);
                  if (url) window.open(url, '_blank', 'noopener,noreferrer');
                }}
              >
                <FiExternalLink /> Explorer
              </button>
            </div>
          ) : null}
        </>
      )}
    </Wrap>
  );
}
